import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, EyeIcon, DownloadIcon, FilterIcon, CreditCardIcon, QrCodeIcon, SmartphoneIcon, FileTextIcon, TerminalIcon, RefreshCwIcon, MonitorIcon, XIcon } from 'lucide-react';
import { Sale, TransactionSource, TransactionStatus } from '../types/Transaction';
export function Sales() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  // Mock data - in a real app, this would come from an API
  const sales: Sale[] = [{
    id: '1',
    transactionId: 'TXN-2024-001234',
    amount: 15000,
    currency: 'LKR',
    status: 'completed',
    source: 'payment_link',
    date: '2024-01-15T10:30:00',
    customerName: 'Nimal Perera',
    customerEmail: 'nimal@example.com',
    customerPhone: '+60 12 345 6789',
    paymentMethod: {
      type: 'card',
      last4: '4242',
      brand: 'Visa'
    },
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    description: 'Online purchase',
    fee: 450,
    netAmount: 14550,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T10:30:00',
      status: 'initiated',
      description: 'Transaction initiated'
    }, {
      id: '2',
      timestamp: '2024-01-15T10:30:15',
      status: 'processing',
      description: 'Payment processing'
    }, {
      id: '3',
      timestamp: '2024-01-15T10:30:45',
      status: 'completed',
      description: 'Payment successful'
    }]
  }, {
    id: '2',
    transactionId: 'TXN-2024-001235',
    amount: 8500,
    currency: 'LKR',
    status: 'completed',
    source: 'qr_code',
    date: '2024-01-15T11:45:00',
    customerName: 'Kamala Silva',
    customerEmail: 'kamala@example.com',
    paymentMethod: {
      type: 'wallet',
      details: 'eZ Cash'
    },
    merchantName: 'XYZ Restaurant',
    merchantId: 'MERCH-002',
    description: 'Dine-in payment',
    fee: 255,
    netAmount: 8245,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T11:45:00',
      status: 'initiated',
      description: 'QR code scanned'
    }, {
      id: '2',
      timestamp: '2024-01-15T11:45:10',
      status: 'completed',
      description: 'Payment successful'
    }]
  }, {
    id: '3',
    transactionId: 'TXN-2024-001236',
    amount: 25000,
    currency: 'LKR',
    status: 'refunded',
    source: 'invoice',
    date: '2024-01-14T14:20:00',
    customerName: 'Saman Fernando',
    customerEmail: 'saman@example.com',
    customerPhone: '+60 13 987 6543',
    paymentMethod: {
      type: 'card',
      last4: '5555',
      brand: 'Mastercard'
    },
    merchantName: 'Tech Solutions Ltd',
    merchantId: 'MERCH-003',
    description: 'Software subscription',
    invoiceId: 'INV-2024-001',
    refundAmount: 25000,
    fee: 750,
    netAmount: 0,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-14T14:20:00',
      status: 'initiated',
      description: 'Invoice payment initiated'
    }, {
      id: '2',
      timestamp: '2024-01-14T14:20:30',
      status: 'completed',
      description: 'Payment successful'
    }, {
      id: '3',
      timestamp: '2024-01-15T09:15:00',
      status: 'refund_initiated',
      description: 'Refund requested'
    }, {
      id: '4',
      timestamp: '2024-01-15T09:20:00',
      status: 'refunded',
      description: 'Refund completed'
    }]
  }, {
    id: '4',
    transactionId: 'TXN-2024-001237',
    amount: 3500,
    currency: 'LKR',
    status: 'completed',
    source: 'tap_to_phone',
    date: '2024-01-15T16:30:00',
    customerName: 'Priya Jayawardena',
    customerEmail: 'priya@example.com',
    paymentMethod: {
      type: 'card',
      last4: '8888',
      brand: 'Visa'
    },
    merchantName: 'Coffee Shop',
    merchantId: 'MERCH-004',
    description: 'Contactless payment',
    fee: 105,
    netAmount: 3395,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T16:30:00',
      status: 'initiated',
      description: 'Card tapped'
    }, {
      id: '2',
      timestamp: '2024-01-15T16:30:05',
      status: 'completed',
      description: 'Payment successful'
    }]
  }, {
    id: '5',
    transactionId: 'TXN-2024-001238',
    amount: 45000,
    currency: 'LKR',
    status: 'failed',
    source: 'pos',
    date: '2024-01-15T17:00:00',
    customerName: 'Ranil Wickramasinghe',
    customerEmail: 'ranil@example.com',
    paymentMethod: {
      type: 'card',
      last4: '3333',
      brand: 'Mastercard'
    },
    merchantName: 'Electronics Store',
    merchantId: 'MERCH-005',
    description: 'POS terminal payment',
    fee: 0,
    netAmount: 0,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T17:00:00',
      status: 'initiated',
      description: 'POS transaction initiated'
    }, {
      id: '2',
      timestamp: '2024-01-15T17:00:10',
      status: 'processing',
      description: 'Contacting bank'
    }, {
      id: '3',
      timestamp: '2024-01-15T17:00:20',
      status: 'failed',
      description: 'Insufficient funds'
    }]
  }];
  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSourceFilter('all');
    setPaymentMethodFilter('all');
    setDateFrom('');
    setDateTo('');
    setAmountMin('');
    setAmountMax('');
  };
  const hasActiveFilters = searchTerm !== '' || statusFilter !== 'all' || sourceFilter !== 'all' || paymentMethodFilter !== 'all' || dateFrom !== '' || dateTo !== '' || amountMin !== '' || amountMax !== '';
  const getSourceIcon = (source: TransactionSource) => {
    const icons = {
      payment_link: <CreditCardIcon size={16} data-id="element-8357" />,
      qr_code: <QrCodeIcon size={16} data-id="element-8358" />,
      tap_to_phone: <SmartphoneIcon size={16} data-id="element-8359" />,
      invoice: <FileTextIcon size={16} data-id="element-8360" />,
      pos: <TerminalIcon size={16} data-id="element-8361" />,
      recurring: <RefreshCwIcon size={16} data-id="element-8362" />,
      virtual_terminal: <MonitorIcon size={16} data-id="element-8363" />
    };
    return icons[source];
  };
  const getSourceLabel = (source: TransactionSource) => {
    const labels = {
      payment_link: 'Payment Link',
      qr_code: 'QR Code',
      tap_to_phone: 'Tap to Phone',
      invoice: 'Invoice',
      pos: 'POS Terminal',
      recurring: 'Recurring',
      virtual_terminal: 'Virtual Terminal'
    };
    return labels[source];
  };
  const getStatusBadge = (status: TransactionStatus) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800',
      partially_refunded: 'bg-orange-100 text-orange-800',
      cancelled: 'bg-gray-100 text-gray-600'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`} data-id="element-8364">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  const filteredSales = sales.filter(sale => {
    const matchesSearch = sale.transactionId.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerName.toLowerCase().includes(searchTerm.toLowerCase()) || sale.customerEmail.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sale.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || sale.source === sourceFilter;
    const matchesPaymentMethod = paymentMethodFilter === 'all' || sale.paymentMethod.type === paymentMethodFilter;
    const saleDate = new Date(sale.date);
    const matchesDateFrom = !dateFrom || saleDate >= new Date(dateFrom);
    const matchesDateTo = !dateTo || saleDate <= new Date(dateTo + 'T23:59:59');
    const matchesAmountMin = !amountMin || sale.amount >= parseFloat(amountMin);
    const matchesAmountMax = !amountMax || sale.amount <= parseFloat(amountMax);
    return matchesSearch && matchesStatus && matchesSource && matchesPaymentMethod && matchesDateFrom && matchesDateTo && matchesAmountMin && matchesAmountMax;
  });
  const totalVolume = filteredSales.reduce((sum, sale) => sum + sale.amount, 0);
  const totalTransactions = filteredSales.length;
  const successfulTransactions = filteredSales.filter(s => s.status === 'completed').length;
  const successRate = totalTransactions > 0 ? Math.round(successfulTransactions / totalTransactions * 100) : 0;
  return <div className="space-y-6" data-id="element-8365">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-8366">
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-8367">
          <div className="text-sm font-medium text-gray-500" data-id="element-8368">Total Volume</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-8369">
            RM {totalVolume.toLocaleString('en-MY')}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-8370">
          <div className="text-sm font-medium text-gray-500" data-id="element-8371">
            Total Transactions
          </div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-8372">
            {totalTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-8373">
          <div className="text-sm font-medium text-gray-500" data-id="element-8374">Successful</div>
          <div className="mt-2 text-3xl font-semibold text-green-600" data-id="element-8375">
            {successfulTransactions}
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg border border-gray-200" data-id="element-8376">
          <div className="text-sm font-medium text-gray-500" data-id="element-8377">Success Rate</div>
          <div className="mt-2 text-3xl font-semibold text-gray-900" data-id="element-8378">
            {successRate}%
          </div>
        </div>
      </div>
      {/* Main Content */}
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-8379">
        {/* Header */}
        <div className="p-6 border-b border-gray-200" data-id="element-8380">
          <div className="space-y-4" data-id="element-8381">
            {/* Primary Filters Row */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-id="element-8382">
              <div className="flex-1 max-w-lg" data-id="element-8383">
                <div className="relative" data-id="element-8384">
                  <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} data-id="element-8385" />
                  <input type="text" placeholder="Search by transaction ID, customer name, or email..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-8386" />
                </div>
              </div>
              <div className="flex items-center gap-3" data-id="element-8387">
                <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${showAdvancedFilters ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`} data-id="element-8388">
                  <FilterIcon size={18} data-id="element-8389" />
                  Filters
                  {hasActiveFilters && <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full" data-id="element-8390">
                      {[searchTerm, statusFilter !== 'all', sourceFilter !== 'all', paymentMethodFilter !== 'all', dateFrom, dateTo, amountMin, amountMax].filter(Boolean).length}
                    </span>}
                </button>
                <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-8391">
                  <DownloadIcon size={18} data-id="element-8392" />
                  Export
                </button>
              </div>
            </div>
            {/* Advanced Filters Panel */}
            {showAdvancedFilters && <div className="p-4 bg-gray-50 rounded-lg space-y-4" data-id="element-8393">
                <div className="flex items-center justify-between" data-id="element-8394">
                  <h3 className="text-sm font-medium text-gray-700" data-id="element-8395">
                    Advanced Filters
                  </h3>
                  {hasActiveFilters && <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700" data-id="element-8396">
                      <XIcon size={16} data-id="element-8397" />
                      Clear all
                    </button>}
                </div>
                {/* Filter Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-8398">
                  {/* Status Filter */}
                  <div data-id="element-8399">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-8400">
                      Status
                    </label>
                    <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-8401">
                      <option value="all" data-id="element-8402">All Status</option>
                      <option value="completed" data-id="element-8403">Completed</option>
                      <option value="pending" data-id="element-8404">Pending</option>
                      <option value="processing" data-id="element-8405">Processing</option>
                      <option value="failed" data-id="element-8406">Failed</option>
                      <option value="refunded" data-id="element-8407">Refunded</option>
                    </select>
                  </div>
                  {/* Source Filter */}
                  <div data-id="element-8408">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-8409">
                      Source
                    </label>
                    <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-8410">
                      <option value="all" data-id="element-8411">All Sources</option>
                      <option value="payment_link" data-id="element-8412">Payment Link</option>
                      <option value="qr_code" data-id="element-8413">QR Code</option>
                      <option value="tap_to_phone" data-id="element-8414">Tap to Phone</option>
                      <option value="invoice" data-id="element-8415">Invoice</option>
                      <option value="pos" data-id="element-8416">POS Terminal</option>
                      <option value="recurring" data-id="element-8417">Recurring</option>
                      <option value="virtual_terminal" data-id="element-8418">Virtual Terminal</option>
                    </select>
                  </div>
                  {/* Payment Method Filter */}
                  <div data-id="element-8419">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-8420">
                      Payment Method
                    </label>
                    <select value={paymentMethodFilter} onChange={e => setPaymentMethodFilter(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-8421">
                      <option value="all" data-id="element-8422">All Methods</option>
                      <option value="card" data-id="element-8423">Card</option>
                      <option value="wallet" data-id="element-8424">Wallet</option>
                      <option value="bank_transfer" data-id="element-8425">Bank Transfer</option>
                      <option value="cash" data-id="element-8426">Cash</option>
                    </select>
                  </div>
                  {/* Date From */}
                  <div data-id="element-8427">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-8428">
                      Date From
                    </label>
                    <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-8429" />
                  </div>
                  {/* Date To */}
                  <div data-id="element-8430">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-8431">
                      Date To
                    </label>
                    <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-8432" />
                  </div>
                  {/* Amount Min */}
                  <div data-id="element-8433">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-8434">
                      Min Amount (RM)
                    </label>
                    <input type="number" placeholder="0" value={amountMin} onChange={e => setAmountMin(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-8435" />
                  </div>
                  {/* Amount Max */}
                  <div data-id="element-8436">
                    <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-8437">
                      Max Amount (RM)
                    </label>
                    <input type="number" placeholder="No limit" value={amountMax} onChange={e => setAmountMax(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm" data-id="element-8438" />
                  </div>
                </div>
              </div>}
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto" data-id="element-8439">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-8440">
            <thead className="bg-gray-50" data-id="element-8441">
              <tr data-id="element-8442">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8443">
                  Transaction ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8444">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8445">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8446">
                  Source
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8447">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8448">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-8449">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-8450">
              {filteredSales.map(sale => <tr key={sale.id} className="hover:bg-gray-50" data-id="element-8451">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-8452">
                    <div className="text-sm font-medium text-gray-900" data-id="element-8453">
                      {sale.transactionId}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-8454">
                    <div className="text-sm text-gray-900" data-id="element-8455">
                      {new Date(sale.date).toLocaleDateString('en-GB')}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-8456">
                      {new Date(sale.date).toLocaleTimeString('en-GB', {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-8457">
                    <div className="text-sm text-gray-900" data-id="element-8458">
                      {sale.customerName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-8459">
                      {sale.customerEmail}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-8460">
                    <div className="flex items-center gap-2 text-sm text-gray-900" data-id="element-8461">
                      {getSourceIcon(sale.source)}
                      {getSourceLabel(sale.source)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-8462">
                    <div className="text-sm font-medium text-gray-900" data-id="element-8463">
                      RM {sale.amount.toLocaleString('en-MY')}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-8464">
                      Net: RM {sale.netAmount.toLocaleString('en-MY')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-8465">
                    {getStatusBadge(sale.status)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-8466">
                    <button onClick={() => navigate(`/sale-detail/${sale.id}`)} className="text-blue-600 hover:text-blue-900" title="View Details" data-id="element-8467">
                      <EyeIcon size={18} data-id="element-8468" />
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        {/* Empty State */}
        {filteredSales.length === 0 && <div className="text-center py-12" data-id="element-8469">
            <p className="text-gray-500" data-id="element-8470">No transactions found</p>
          </div>}
      </div>
    </div>;
}